import { z } from 'zod';

/**
 * Reusable field-level validators encoding the api-contract §6 rules. Defined
 * once so every entity DTO stays consistent (no copy-pasted limits drifting).
 */

/** UUID v4 string — every `*Id` on the wire (api-contract §0.2). */
export const uuid = () => z.string().uuid();

/**
 * Display name for lists/items/activities: trimmed, min length 1, max 200
 * (api-contract §6). Trimming happens before length checks so " " is rejected.
 */
export const name = () => z.string().trim().min(1).max(200);

/** Nullable long-form text (description / prompt): trimmed, max 2000. */
export const longText = () => z.string().trim().max(2000).nullable();

/**
 * Free-keyword label list (master_list_items.default_labels): trimmed,
 * non-empty strings, deduped, max 50 entries (api-contract §6).
 */
export const labelArray = () =>
  z
    .array(z.string().trim().min(1))
    .max(50)
    .transform((labels) => Array.from(new Set(labels)));

/* -------------------------------------------------------------------------- */
/* Path param schemas (api-contract §0.8)                                      */
/* -------------------------------------------------------------------------- */

/** `{ listId }` — Master List routes (api-contract §2). */
export const ListIdParam = z.object({ listId: uuid() });
export type ListIdParam = z.infer<typeof ListIdParam>;

/** `{ listId, itemId }` — Master List Item routes (api-contract §3). */
export const ItemIdParam = z.object({ listId: uuid(), itemId: uuid() });
export type ItemIdParam = z.infer<typeof ItemIdParam>;

/** `{ activityId }` — Activity routes (api-contract §4). */
export const ActivityIdParam = z.object({ activityId: uuid() });
export type ActivityIdParam = z.infer<typeof ActivityIdParam>;

/** `{ activityId, masterListId }` — Activity ↔ List detach (api-contract §4.7). */
export const ActivityListParam = z.object({
  activityId: uuid(),
  masterListId: uuid(),
});
export type ActivityListParam = z.infer<typeof ActivityListParam>;

/** `{ tripId }` — Trip routes (Execution Plan T14–T18). */
export const TripIdParam = z.object({ tripId: uuid() });
export type TripIdParam = z.infer<typeof TripIdParam>;

/** `{ tripId, itemId }` — Trip Item routes (Execution Plan T16). */
export const TripItemIdParam = z.object({ tripId: uuid(), itemId: uuid() });
export type TripItemIdParam = z.infer<typeof TripItemIdParam>;

/** `{ lessonId }` — Lesson routes (Execution Plan T33–T35). */
export const LessonIdParam = z.object({ lessonId: uuid() });
export type LessonIdParam = z.infer<typeof LessonIdParam>;
